/*
 * dd-free-gift-nudge.js — "Order above ₹200 and get 1 free gift 🎁"
 *
 * Markup + settings: sections/dd-free-gift-nudge.liquid (rendered from layout/theme.liquid).
 *
 * WHAT IT DOES
 *   Home / Collection → popup (card on desktop, bottom sheet on phones)
 *   Product page      → small non-blocking bottom banner, lifted above any sticky bar
 *   Copy follows the cart:
 *     empty            "Order above ₹200 and get 1 free gift 🎁"
 *     below threshold  "Add ₹65 more to unlock your free gift"   (+ progress bar)
 *     unlocked         popup/banner is skipped; if it is already open it turns green
 *
 * CART
 *   Reads through window.ddCart (assets/dd-cart-state.js) — no new fetch patching,
 *   no new polling. Falls back to one /cart.js read if ddCart is missing.
 *   ₹1 gift lines (product type in cfg.giftTypes) are excluded from the total.
 *
 * FREQUENCY CAP
 *   Cookie `dd_fg` = comma list of page types (index, collection, product) already shown this session.
 *   max-age = sessionMin minutes, refreshed on every page view (sliding window,
 *   shared across tabs). Recorded only when something is actually shown, so a
 *   visitor whose cart is already unlocked is never "charged" a view.
 *
 * ANALYTICS (dataLayer)
 *   dd_fg_view  { fg_surface, fg_state, fg_remaining }
 *   dd_fg_click { fg_surface, fg_action: 'cta'|'view_cart' }
 *   dd_fg_close { fg_surface, fg_reason: 'x'|'backdrop'|'esc'|'keep'|'auto' }
 */
(function () {
  'use strict';

  var COOKIE = 'dd_fg';

  /* ───────────── pure helpers (also exported for tests) ───────────── */

  function normTypes(list) {
    return (list || []).map(function (t) { return String(t || '').trim().toLowerCase(); })
      .filter(Boolean);
  }

  function isGiftLine(item, giftTypes) {
    return giftTypes.indexOf(String(item.product_type || '').trim().toLowerCase()) !== -1;
  }

  /* Cart value that counts towards the threshold, in paise. Uses the price the
     shopper actually pays for each line (after line-level discounts). */
  function qualifyingPaise(cart, giftTypes) {
    if (!cart || !cart.items) return 0;
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftLine(it, giftTypes)) continue;
      var v = typeof it.final_line_price === 'number' ? it.final_line_price
            : typeof it.line_price === 'number' ? it.line_price
            : (it.price || 0) * (it.quantity || 0);
      sum += v;
    }
    return sum;
  }

  function stateFor(qualifying, thresholdPaise) {
    if (qualifying >= thresholdPaise) return { key: 'unlocked', have: qualifying, remaining: 0, pct: 100 };
    if (qualifying <= 0) return { key: 'empty', have: 0, remaining: thresholdPaise, pct: 0 };
    return {
      key: 'partial',
      have: qualifying,
      remaining: thresholdPaise - qualifying,
      pct: Math.min(96, Math.max(6, Math.round(qualifying / thresholdPaise * 100)))
    };
  }

  /* ₹ rounded UP to the rupee — "add ₹64.50 more" reads as "add ₹65 more",
     which is always enough. */
  function rupees(paise) {
    var r = Math.ceil(Math.max(0, paise) / 100);
    try { return '₹' + r.toLocaleString('en-IN'); } catch (e) { return '₹' + r; }
  }

  function parseSeen(cookieStr) {
    var m = String(cookieStr || '').match(/(?:^|;\s*)dd_fg=([^;]*)/);
    if (!m) return null;
    var v = decodeURIComponent(m[1]);
    return v === '-' || v === '' ? [] : v.split(',');
  }

  function serializeSeen(list, minutes) {
    return COOKIE + '=' + encodeURIComponent(list.length ? list.join(',') : '-') +
      '; path=/; max-age=' + Math.round(minutes * 60) + '; SameSite=Lax';
  }

  /* per page type: 'index' | 'collection' | 'product' — so Home and Collection each get their one showing */
  function capKey(cfg) { return cfg.capMode === 'global' ? 'any' : (cfg.page || cfg.surface); }

  var api = {
    isGiftLine: isGiftLine, qualifyingPaise: qualifyingPaise, stateFor: stateFor,
    rupees: rupees, parseSeen: parseSeen, serializeSeen: serializeSeen, capKey: capKey
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  window.ddFreeGift = api;

  /* ───────────── boot ───────────── */

  var root = document.getElementById('dd-fg');
  if (!root || root.__fgBooted) return;
  root.__fgBooted = true;

  var cfg;
  try { cfg = JSON.parse(root.querySelector('[data-fg-config]').textContent); } catch (e) { return; }
  if (!cfg.surface) return;

  var giftTypes = normTypes(cfg.giftTypes);
  var T = Math.round((Number(cfg.threshold) || 200) * 100);
  var sessionMin = Number(cfg.sessionMin) || 30;
  var params;
  try { params = new URLSearchParams(location.search); } catch (e) { params = { get: function () { return null; } }; }

  if (params.get('fg_reset') === '1') document.cookie = COOKIE + '=; path=/; max-age=0; SameSite=Lax';
  var force = params.get('fg_debug') === '1' || !!cfg.designMode;

  var seen = parseSeen(document.cookie) || [];
  try { document.cookie = serializeSeen(seen, sessionMin); } catch (e) {}   /* slide the session window */

  var KEY = capKey(cfg);
  if (!force && seen.indexOf(KEY) !== -1) return;   /* already shown this session */

  var isModal = cfg.surface === 'modal';
  var $ = function (sel) { return root.querySelector(sel); };
  var els = {
    title: $('[data-fg-title]'), sub: $('[data-fg-sub]'), hint: $('[data-fg-hint]'),
    progress: $('[data-fg-progress]'), fill: $('[data-fg-fill]'), have: $('[data-fg-have]'),
    cta: $('[data-fg-cta]'), keep: $('[data-fg-keep]'),
    dialog: $('.dd-fg__modal')
  };

  var current = null, open = false, unsub = null, lastFocus = null, prevOverflow = '', offsetTimer = 0;

  function track(event, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      var o = { event: event, fg_surface: cfg.surface };
      for (var k in extra) o[k] = extra[k];
      window.dataLayer.push(o);
    } catch (e) {}
  }

  function getCart() {
    if (window.ddCart && typeof window.ddCart.get === 'function') return window.ddCart.get();
    return fetch((cfg.cartUrl || '/cart') + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function nudgeText(st) { return String(cfg.nudge || '').replace(/\[amount\]/g, rupees(st.remaining)); }

  /* ───────────── render ───────────── */

  function render(st) {
    current = st;
    root.classList.toggle('is-unlocked', st.key === 'unlocked');
    var title = st.key === 'empty' ? cfg.headline : st.key === 'partial' ? nudgeText(st) : cfg.unlocked;
    if (els.title) els.title.textContent = title;
    if (els.fill) els.fill.style.width = st.pct + '%';

    if (isModal) {
      if (els.sub) {
        els.sub.textContent = cfg.sub || '';
        els.sub.hidden = st.key === 'unlocked' || !cfg.sub;
      }
      if (els.progress) els.progress.hidden = st.key === 'empty';
      if (els.have) els.have.textContent = rupees(st.have) + ' in cart';
      if (els.cta) els.cta.textContent = st.key === 'empty' ? (cfg.ctaEmpty || 'Start shopping') : 'View cart';
      if (els.keep) els.keep.hidden = st.key === 'empty';
    } else {
      /* PDP: "Adding this item unlocks it ✨" when this product alone closes the gap */
      var price = Number(cfg.productPrice) || 0;
      var showHint = st.key !== 'unlocked' && price > 0 && price >= st.remaining && !!cfg.pdpHint;
      if (els.hint) { els.hint.textContent = cfg.pdpHint || ''; els.hint.hidden = !showHint; }
      if (els.cta) els.cta.hidden = st.key !== 'partial';
    }
  }

  /* ───────────── PDP banner: sit above sticky add-to-cart / bottom bars ───────────── */

  function fixedAncestor(el) {
    while (el && el !== document.body && el !== document.documentElement) {
      var p = getComputedStyle(el).position;
      if (p === 'fixed' || p === 'sticky') return el;
      el = el.parentElement;
    }
    return null;
  }

  function measureOffset() {
    if (!document.elementsFromPoint) return 0;
    var vh = window.innerHeight, vw = window.innerWidth, max = 0;
    var xs = [vw * 0.2, vw * 0.5, vw * 0.8];
    for (var i = 0; i < xs.length; i++) {
      var stack = document.elementsFromPoint(xs[i], vh - 2) || [];
      for (var j = 0; j < stack.length; j++) {
        var el = stack[j];
        if (root.contains(el)) continue;
        var f = fixedAncestor(el);
        if (!f || root.contains(f)) continue;
        var r = f.getBoundingClientRect();
        if (r.height > 0 && r.height < vh * 0.4 && r.bottom >= vh - 4) max = Math.max(max, vh - r.top);
        break;
      }
    }
    return Math.min(Math.round(max), 220);
  }

  function updateOffset() {
    offsetTimer = 0;
    root.style.setProperty('--fg-offset', measureOffset() + 'px');
  }
  function scheduleOffset() { if (!offsetTimer) offsetTimer = setTimeout(updateOffset, 250); }

  /* ───────────── open / close ───────────── */

  function onKey(e) {
    if (!open || !isModal) return;
    if (e.key === 'Escape') { close('esc'); return; }
    if (e.key !== 'Tab') return;
    var f = Array.prototype.filter.call(
      els.dialog.querySelectorAll('button:not([hidden]), a[href]:not([hidden])'),
      function (n) { return n.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function show(st) {
    if (open) return;
    open = true;

    if (!force) { seen.push(KEY); try { document.cookie = serializeSeen(seen, sessionMin); } catch (e) {} }

    render(st);
    root.hidden = false;

    if (isModal) {
      lastFocus = document.activeElement;
      prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey, true);
    } else {
      updateOffset();
      window.addEventListener('scroll', scheduleOffset, { passive: true });
      window.addEventListener('resize', scheduleOffset, { passive: true });
      setTimeout(updateOffset, 1200);   /* sticky ATC bars often appear a moment later */
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('is-open');
        if (isModal && els.dialog) { try { els.dialog.focus({ preventScroll: true }); } catch (e) { els.dialog.focus(); } }
      });
    });

    if (window.ddCart && typeof window.ddCart.subscribe === 'function') {
      unsub = window.ddCart.subscribe(function (cart) {
        if (!open) return;
        var next = stateFor(qualifyingPaise(cart, giftTypes), T);
        if (current && next.key === current.key && next.have === current.have) return;
        render(next);
        if (!isModal && next.key === 'unlocked') setTimeout(function () { close('auto'); }, 5000);
      });
    }

    track('dd_fg_view', { fg_state: st.key, fg_remaining: Math.ceil(st.remaining / 100) });
  }

  function close(reason) {
    if (!open) return;
    open = false;
    root.classList.remove('is-open');
    if (unsub) { try { unsub(); } catch (e) {} unsub = null; }

    if (isModal) {
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey, true);
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
    } else {
      window.removeEventListener('scroll', scheduleOffset);
      window.removeEventListener('resize', scheduleOffset);
    }
    setTimeout(function () { if (!open) root.hidden = true; }, 360);
    track('dd_fg_close', { fg_reason: reason });
  }

  function openCart() {
    var icon = document.querySelector('#cart-icon-bubble, .dd-hd-cart, .header__icon--cart');
    if (icon) { icon.click(); return; }   /* GoKwik / theme drawer takes over */
    location.href = cfg.cartUrl || '/cart';
  }

  root.addEventListener('click', function (e) {
    var closer = e.target.closest('[data-fg-close]');
    if (closer) { e.preventDefault(); close(closer.getAttribute('data-fg-close')); return; }

    var cta = e.target.closest('[data-fg-cta]');
    if (!cta) return;
    e.preventDefault();
    var st = current ? current.key : 'empty';
    if (isModal && st === 'empty') {
      track('dd_fg_click', { fg_action: 'cta' });
      close('cta');
      if (cfg.ctaEmptyUrl) location.href = cfg.ctaEmptyUrl;
    } else {
      track('dd_fg_click', { fg_action: 'view_cart' });
      close('cta');
      setTimeout(openCart, isModal ? 120 : 0);
    }
  });

  /* ───────────── when to show ───────────── */

  function cartDrawerOpen() {
    return document.body.classList.contains('gk-cart-open') ||
      !!document.querySelector('cart-drawer.active, cart-drawer[open], .drawer.active');
  }

  /* Never pop over an open cart drawer or a background tab — wait for calm. */
  function whenCalm(fn, tries) {
    tries = tries || 0;
    if (tries < 40 && (document.visibilityState !== 'visible' || cartDrawerOpen())) {
      setTimeout(function () { whenCalm(fn, tries + 1); }, 1500);
      return;
    }
    fn();
  }

  var fired = false, delayTimer = 0;
  function onScroll() {
    var h = document.documentElement.scrollHeight - window.innerHeight;
    if (h > 0 && window.scrollY / h >= (Number(cfg.scrollPct) || 0.35)) trigger();
  }

  function trigger() {
    if (fired) return;
    fired = true;
    clearTimeout(delayTimer);
    window.removeEventListener('scroll', onScroll);
    whenCalm(function () {
      getCart().then(function (cart) {
        var st = stateFor(qualifyingPaise(cart, giftTypes), T);
        if (st.key === 'unlocked' && !force) return;   /* nothing to nudge; keep the cap unspent */
        show(st);
      });
    });
  }

  function arm() {
    if (force) { delayTimer = setTimeout(trigger, 300); return; }
    if (isModal) {
      delayTimer = setTimeout(trigger, Number(cfg.delayMs) || 4000);
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      delayTimer = setTimeout(trigger, Number(cfg.pdpDelayMs) || 3000);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();

  /* Theme editor: re-render on setting changes */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#dd-fg')) location.reload();
  });
})();
